import { useEffect, useState, useMemo } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, X } from "lucide-react";
import { Button } from "../ui/button";
import { getAuthUser, logoutUser } from "../../utils/auth";
import { getSiteSettings, fetchSiteSettings } from "../../utils/siteSettings";
import { applyTextOverridesToDom, attachAdminInlineTextEditing, fetchTextOverrides } from "../../utils/textOverrides";
import { getCartItems } from "../../utils/cart";

// Countdown hook
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState("");
  useEffect(() => {
    if (!targetDate) return;
    const target = new Date(targetDate).getTime();
    const update = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) { setTimeLeft(""); return; }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(`${d.toString().padStart(2,"0")}:${h.toString().padStart(2,"0")}:${m.toString().padStart(2,"0")}:${s.toString().padStart(2,"0")}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);
  return timeLeft;
}

const baseNavItems = [
  { label: "Home", path: "/" },
  { label: "Tiny Treasures", path: "/tiny-treasures" },
  { label: "Shop All", path: "/shop-all" },
  { label: "Bath Salts", path: "/bath-salts" },
  { label: "Body Oils", path: "/body-oils" },
  { label: "Our Story", path: "/our-story" },
];

const logoNoBg =
  "https://customer-assets.emergentagent.com/job_nature-science-skin/artifacts/gwov0pwr_Logo_No%20background.png";

const getNavClass = ({ isActive }) =>
  `ekman-nav-link ${isActive ? "text-black underline decoration-1 underline-offset-8" : "text-black/70"}`;

export default function EkmanLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [showReturnsPolicy, setShowReturnsPolicy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const authUser = getAuthUser();
  const siteSettings = getSiteSettings();

  const navItems = [
    ...baseNavItems,
    ...(authUser?.role === "customer"
      ? [
          { label: "My Orders", path: "/my-orders" },
          { label: "Help", path: "/help" },
        ]
      : []),
    ...(authUser?.role === "admin" ? [{ label: "Admin", path: "/admin" }] : []),
  ];

  useEffect(() => {
    const updateCartCount = () => {
      const items = getCartItems();
      const total = items.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(total);
    };
    updateCartCount();
    window.addEventListener("cart-updated", updateCartCount);
    return () => window.removeEventListener("cart-updated", updateCartCount);
  }, []);

  useEffect(() => {
    // Apply cached text overrides immediately (sync)
    applyTextOverridesToDom(document);
    
    // Background fetch (only happens once per session due to internal caching)
    fetchTextOverrides().then(() => applyTextOverridesToDom(document));
    fetchSiteSettings();
  }, []);

  useEffect(() => {
    if (authUser?.role === "admin") {
      return attachAdminInlineTextEditing(document);
    }
  }, [authUser?.role]);

  const closeMobileMenu = () => {
    setMenuClosing(true);
    setTimeout(() => {
      setMobileMenuOpen(false);
      setMenuClosing(false);
    }, 250);
  };

  const onLogout = () => {
    logoutUser();
    closeMobileMenu();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#E5E1D8]" data-testid="ekman-layout-root">
      {/* Returns Policy Modal */}
      {showReturnsPolicy && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowReturnsPolicy(false)}>
          <div className="w-full max-w-md rounded-2xl bg-[#E5E1D8] p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-playfair text-2xl">No Returns Policy</h2>
            <p className="mt-4 text-sm text-black/80">At Ekman, we maintain a <strong>strict No Returns policy</strong>.</p>
            <p className="mt-2 text-sm text-black/80">We do not accept any return requests or refunds once an order has been placed and shipped.</p>
            <p className="mt-2 text-sm text-black/80">Please review your order carefully before completing your purchase.</p>
            <p className="mt-4 text-xs text-black/60">For any queries, contact us at {siteSettings.customerCareEmail}</p>
            <button onClick={() => setShowReturnsPolicy(false)} className="mt-4 w-full rounded-full bg-black py-2 text-sm text-[#E5E1D8]">Close</button>
          </div>
        </div>
      )}
      
      <div className="sticky top-0 z-50">
        {/* Top Banner with Countdown */}
        <TopBanner settings={siteSettings} />
        
        <header
          className="border-b border-black/10 bg-[#E5E1D8]/95 px-4 py-3 backdrop-blur-md md:px-8"
          data-testid="site-header"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <Button
              variant="ghost"
              className="h-9 w-9 rounded-full p-0 md:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-open-button"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <nav className="hidden items-center gap-6 md:flex" data-testid="desktop-primary-nav">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={getNavClass}
                  data-testid={`desktop-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <Link
              to="/"
              className="inline-flex items-center"
              data-testid="brand-logo-link"
            >
              <img
                src={logoNoBg}
                alt="Ekman"
                className="h-10 w-auto object-contain md:h-12"
                data-testid="brand-logo-image"
              />
            </Link>

            <div className="flex items-center gap-2" data-testid="header-utility-actions">
              <Button
                asChild
                variant="outline"
                className="hidden h-9 rounded-full border-black bg-transparent px-4 text-xs uppercase tracking-[0.12em] md:inline-flex"
                data-testid="header-login-button"
              >
                {authUser ? (
                  <button type="button" onClick={onLogout} data-testid="header-logout-button">
                    Logout
                  </button>
                ) : (
                  <Link to="/login" data-testid="header-login-link">
                    Login
                  </Link>
                )}
              </Button>
              {authUser && (
                <span className="hidden text-xs text-black/60 md:inline" data-testid="header-user-id">
                  Hi, {authUser.name?.split(" ")[0] || authUser.id}
                </span>
              )}
              <Button
                asChild
                variant="ghost"
                className="h-9 w-9 rounded-full p-0"
                data-testid="header-search-button"
              >
                <Link to="/search" aria-label="Search products">
                  <Search className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                className="relative h-9 w-9 rounded-full p-0"
                data-testid="header-bag-button"
              >
                <Link to="/checkout" aria-label="Open checkout">
                  <ShoppingBag className="h-4 w-4" />
                  {cartCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-black text-[10px] text-[#E5E1D8]" data-testid="cart-count-badge">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </Button>
            </div>
          </div>
        </header>
      </div>

      {mobileMenuOpen && (
        <div className={`fixed inset-0 z-[60] md:hidden ${menuClosing ? "animate-fade-out" : "animate-fade-in"}`} data-testid="mobile-menu-overlay">
          <button
            className={`absolute inset-0 bg-black/35 ${menuClosing ? "animate-fade-out" : "animate-fade-in"}`}
            onClick={closeMobileMenu}
            data-testid="mobile-menu-backdrop"
            aria-label="Close menu"
          />
          <aside
            className={`relative h-full w-[80%] max-w-xs bg-[#EFEBE3] px-5 py-6 ${menuClosing ? "animate-slide-out-left" : "animate-slide-in-left"}`}
            data-testid="mobile-menu-drawer"
          >
            <div className="mb-8 flex items-center justify-between">
              <img src={logoNoBg} alt="Ekman" className="h-10 w-auto object-contain" data-testid="mobile-menu-logo-image" />
              <Button
                variant="ghost"
                className="h-8 w-8 rounded-full p-0"
                onClick={closeMobileMenu}
                data-testid="mobile-menu-close-button"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <nav className="space-y-4" data-testid="mobile-menu-nav-list">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className="block border-b border-black/10 pb-2 text-sm uppercase tracking-[0.14em]"
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </NavLink>
              ))}
              {authUser ? (
                <button
                  type="button"
                  onClick={onLogout}
                  className="block w-full border-b border-black/10 pb-2 text-left text-sm uppercase tracking-[0.14em]"
                  data-testid="mobile-logout-button"
                >
                  Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="block border-b border-black/10 pb-2 text-sm uppercase tracking-[0.14em]"
                  data-testid="mobile-login-link"
                >
                  Login
                </Link>
              )}
            </nav>
          </aside>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 pb-20 pt-6 md:px-8 overflow-x-hidden" data-testid="main-content-wrapper">
        <Outlet />
      </main>

      <footer className="border-t border-black/10 px-4 py-10 md:px-8" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-3">
          <div data-testid="footer-brand-block">
            <img src={logoNoBg} alt="Ekman" className="h-10 w-auto object-contain" data-testid="footer-brand-logo-image" />
            <p className="mt-2 text-sm text-black/70" data-testid="footer-brand-description">
              Nature-led skincare, crafted for modern rituals.
            </p>
          </div>
          <div data-testid="footer-links-block">
            <p className="text-xs uppercase tracking-[0.18em]" data-testid="footer-links-heading">
              Quick Links
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="text-sm text-black/75 transition-colors hover:text-black"
                  data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => setShowReturnsPolicy(true)}
                className="text-sm text-black/75 transition-colors hover:text-black underline"
                data-testid="footer-link-returns-policy"
              >
                No Returns Policy
              </button>
              <button
                type="button"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/catalogue.pdf`, '_blank')}
                className="text-sm text-black/75 transition-colors hover:text-black underline"
                data-testid="footer-link-catalogue"
              >
                Download Catalogue
              </button>
            </div>
          </div>
          <div data-testid="footer-contact-block">
            <p className="text-xs uppercase tracking-[0.18em]" data-testid="footer-contact-heading" data-no-text-edit="false">
              {siteSettings.customerCareTitle}
            </p>
            <p className="mt-3 text-sm text-black/75" data-testid="footer-contact-email">
              {siteSettings.customerCareEmail}
            </p>
            <p className="text-sm text-black/75" data-testid="footer-contact-hours">
              {siteSettings.customerCareHours}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TopBanner({ settings }) {
  const countdown = useCountdown(settings.topBannerCountdown);
  const textSize = settings.topBannerSize || "text-xs";
  const bannerText = settings.topBannerText || "Welcome to Ekman.";
  
  return (
    <div className={`bg-black px-4 py-2 text-center ${textSize} tracking-[0.16em] text-[#E5E1D8] flex items-center justify-center gap-3`} data-testid="top-banner">
      <span>{bannerText}</span>
      {countdown && <span className="font-mono bg-white/10 px-2 py-0.5 rounded">{countdown}</span>}
    </div>
  );
}